import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'clsx';
import { TextStyled } from '@nike/nike-design-system-components';
import { CLICK_MOD_EVENTS } from '@nike/ciclp-utils';
import { VideoCard } from '../videoCard';
import { CARD_TYPES, TEXT_POSITIONS, ANIMATION_TYPES } from './constants';
import styles from './stickyContent.styl';

const CMS_TO_STICKY_TYPE = {
  image: CARD_TYPES.IMAGE,
  text: CARD_TYPES.COLOR,
  video: CARD_TYPES.VIDEO,
};

const mapSlidesToStickyCards = (slides = []) =>
  slides.map(slide => {
    const type =
      CMS_TO_STICKY_TYPE[slide.containerType?.toLowerCase()] ||
      CARD_TYPES.COLOR;

    return {
      ...slide,
      backgroundColor: slide.backgroundColor || '#000000',
      backgroundImage:
        slide.landscapeURL ||
        slide.portraitURL ||
        slide.landscapePosterUrl ||
        slide.portraitPosterUrl ||
        undefined,
      body: slide.bodyProps?.text || undefined,
      subtitle: slide.subtitleProps?.text || undefined,
      title: slide.titleProps?.text || undefined,
      type,
    };
  });

const suppressTextProps = {
  actionButtons: [],
  bodyProps: { text: '' },
  subtitleProps: { text: '' },
  titleProps: { text: '' },
};

const StickyContentCard = ({ card, index, hasFadeInContentInternalName }) => {
  const cardRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Observer for sticky detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(entry.boundingClientRect.y <= 0 && entry.isIntersecting);
      },
      {
        rootMargin: '0px',
        threshold: [0, 1],
      },
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  // Observer for animation at 50% visibility (only for first card)
  useEffect(() => {
    if (index !== 0 || hasAnimated) return undefined;

    const animationObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          setHasAnimated(true);
        }
      },
      {
        rootMargin: '0px',
        threshold: [0.5],
      },
    );

    if (cardRef.current) {
      animationObserver.observe(cardRef.current);
    }

    return () => {
      if (animationObserver) {
        animationObserver.disconnect();
      }
    };
  }, [index, hasAnimated]);

  // Observer for analytics viewed event at 50% visibility
  useEffect(() => {
    const viewedObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          CLICK_MOD_EVENTS.trackTemporaryView('stickyCardViewed', {
            card_id: String(card.id).slice(0, 100),
            index: String(index),
          });
        }
      },
      {
        rootMargin: '0px',
        threshold: 0.5,
      },
    );

    if (cardRef.current) {
      viewedObserver.observe(cardRef.current);
    }

    return () => {
      if (viewedObserver) {
        viewedObserver.disconnect();
      }
    };
  }, [card.id, index]);

  const isVideo = card.type === CARD_TYPES.VIDEO;

  const cardStyle = {
    backgroundColor: card.backgroundColor || '#000000',
    zIndex: index + 1,
  };

  const shouldRenderFirstCardImage =
    index === 0 && hasFadeInContentInternalName && card.backgroundImage;

  return (
    <div
      ref={cardRef}
      className={styles.stickyCard}
      style={cardStyle}
      data-card-id={card.id}
      data-is-stuck={isStuck}
    >
      {/* Background image for non-video cards, or as poster/fallback */}
      {!isVideo && card.backgroundImage && !shouldRenderFirstCardImage && (
        <img
          className={styles.videoBackground}
          src={card.backgroundImage}
          alt={card.title || card.subtitle || ''}
          loading={index === 0 ? 'eager' : 'lazy'}
        />
      )}

      {/* Video background for VIDEO type cards using VideoCard (same as animatedContent/autoplayCarousel) */}
      {isVideo && (
        <div className={styles.cardBackground}>
          <VideoCard
            {...card}
            {...suppressTextProps}
            preset="ambient"
            loop
            dataAnalyticsHasLandmark={false}
          />
        </div>
      )}

      {/* Card content */}
      <div
        className={cx(styles.cardContent, {
          [styles.fadeInContent]: index === 0,
          [styles.animateIn]: index === 0 && hasAnimated,
          [styles.textLeft]: index !== 0,
        })}
      >
        {shouldRenderFirstCardImage ? (
          <img
            src={card.backgroundImage}
            alt={[card.subtitle, card.title].filter(Boolean).join(' - ') || ''}
            className={styles.cardTextImage}
          />
        ) : (
          <>
            {card.title && (
              <TextStyled
                Component="h2"
                appearance="body1"
                className={cx(styles.cardTitle, styles.quoteTitle)}
              >
                {card.title}
              </TextStyled>
            )}
            {card.subtitle && (
              <TextStyled
                Component="h3"
                appearance="body1"
                className={cx(styles.cardSubtitle, styles.quoteSubtitle)}
              >
                {card.subtitle}
              </TextStyled>
            )}
          </>
        )}
        {card.body && (
          <TextStyled
            Component="p"
            appearance="body1"
            color={card.bodyColor || 'primaryInverse'}
            className={styles.cardBody}
          >
            {card.body}
          </TextStyled>
        )}
      </div>
    </div>
  );
};

StickyContentCard.propTypes = {
  card: PropTypes.shape({
    backgroundColor: PropTypes.string,
    backgroundImage: PropTypes.string,
    body: PropTypes.string,
    bodyColor: PropTypes.string,
    containerType: PropTypes.string,
    id: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    subtitleColor: PropTypes.string,
    title: PropTypes.string,
    titleColor: PropTypes.string,
    type: PropTypes.oneOf(Object.values(CARD_TYPES)),
    videoId: PropTypes.string,
  }).isRequired,
  hasFadeInContentInternalName: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
};

export const StickyContent = ({
  slides = [],
  cards = [],
  internalName = '',
  analyticsItemId,
  hashKey,
}) => {
  const resolvedCards =
    slides && slides.length > 0 ? mapSlidesToStickyCards(slides) : cards;

  if (!resolvedCards || resolvedCards.length === 0) {
    return null;
  }

  const hasFadeInContentInternalName = internalName.includes('fade-in-content');

  return (
    <div
      className={styles.stickyContainer}
      data-testid="sticky-content"
      data-analytics-placement-id={analyticsItemId}
      data-click-mod={hashKey}
    >
      {resolvedCards.map((card, index) => (
        <StickyContentCard
          key={card.id}
          card={card}
          index={index}
          hasFadeInContentInternalName={hasFadeInContentInternalName}
        />
      ))}
    </div>
  );
};

StickyContent.propTypes = {
  analyticsItemId: PropTypes.string,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      animationType: PropTypes.oneOf(Object.values(ANIMATION_TYPES)),
      backgroundColor: PropTypes.string,
      backgroundImage: PropTypes.string,
      body: PropTypes.string,
      bodyColor: PropTypes.string,
      containerType: PropTypes.string,
      id: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      subtitleColor: PropTypes.string,
      textPosition: PropTypes.oneOf(Object.values(TEXT_POSITIONS)),
      title: PropTypes.string,
      titleColor: PropTypes.string,
      type: PropTypes.oneOf(Object.values(CARD_TYPES)),
      videoId: PropTypes.string,
    }),
  ).isRequired,
  hashKey: PropTypes.string,
  internalName: PropTypes.string,
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      backgroundColor: PropTypes.string,
      bodyProps: PropTypes.shape({
        text: PropTypes.string,
      }),
      containerType: PropTypes.string,
      id: PropTypes.string.isRequired,
      landscapePosterUrl: PropTypes.string,
      landscapeURL: PropTypes.string,
      portraitPosterUrl: PropTypes.string,
      portraitURL: PropTypes.string,
      subtitleProps: PropTypes.shape({
        text: PropTypes.string,
      }),
      titleProps: PropTypes.shape({
        text: PropTypes.string,
      }),
    }),
  ),
};

export default StickyContent;
